import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { Loader, ChevronLeft, Package, IndianRupee, ExternalLink, MapPin, Phone, User, CheckCircle2, XCircle, Clock, AlertTriangle, Tag, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Order {
  id: string;
  user_id: string | null;
  collectible_id: string;
  full_name: string;
  phone: string;
  address_line1: string;
  address_line2: string | null;
  city: string;
  state: string;
  pincode: string;
  created_at: string;
  status: 'received' | 'fulfilled' | 'cancelled';
  collectible: {
    id: string;
    name: string;
    type: 'coin' | 'note';
    price: number;
    slug: string;
    status: string;
    images: {
      id: string;
      image_url: string;
      order: number;
    }[];
  };
}

function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const checkAdminAndFetchOrders = async () => {
      try {
        // Check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!profile?.is_admin) {
          throw new Error('Not authorized');
        }

        setIsAdmin(true);

        // Fetch orders with collectible details
        const { data: ordersData, error: ordersError } = await supabase
          .from('delivery_addresses')
          .select(`
            *,
            collectible:collectibles (
              id,
              name,
              type,
              price,
              slug,
              status,
              images:collectible_images (
                id,
                image_url,
                order
              )
            )
          `)
          .order('created_at', { ascending: false });

        if (ordersError) throw ordersError;
        setOrders(ordersData || []);
        setFilteredOrders(ordersData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchOrders();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = orders.filter(order => 
      order.full_name.toLowerCase().includes(query) ||
      order.phone.includes(query) ||
      order.collectible.name.toLowerCase().includes(query) ||
      order.id.toLowerCase().includes(query) ||
      order.status.toLowerCase().includes(query) ||
      order.address_line1.toLowerCase().includes(query) ||
      order.city.toLowerCase().includes(query) ||
      order.state.toLowerCase().includes(query) ||
      order.pincode.includes(query)
    );

    setFilteredOrders(filtered);
  }, [searchQuery, orders]);

  const updateOrderStatus = async (orderId: string, status: 'received' | 'fulfilled' | 'cancelled') => {
    try {
      setUpdatingStatus(orderId);

      // Update order status
      const { error: statusError } = await supabase
        .from('delivery_addresses')
        .update({ status })
        .eq('id', orderId);

      if (statusError) throw statusError;

      // Update local state
      setOrders(prev => prev.map(o => {
        if (o.id === orderId) {
          return {
            ...o,
            status
          };
        }
        return o;
      }));
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'received':
        return <Clock className="h-5 w-5 text-blue-500" />;
      case 'fulfilled':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'received':
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'fulfilled':
        return 'bg-green-50 text-green-700 border-green-100';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
          <Link to="/" className="text-primary-600 hover:text-primary-800 font-medium">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Orders Dashboard - Admin</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="sr-only sm:not-sr-only">Back to Home</span>
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Orders Dashboard</h1>
          </div>
          <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
            {filteredOrders.length} Orders
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search orders by name, phone, address..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No matching orders found' : 'No Orders Yet'}
            </h3>
            <p className="text-gray-600">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Orders will appear here when customers make purchases.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                    <div className="w-full sm:w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {order.collectible.images?.[0] && (
                        <img
                          src={order.collectible.images[0].image_url}
                          alt={order.collectible.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    <div className="flex-grow">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                        <div>
                          <Link
                            to={`/${order.collectible.slug}`}
                            className="text-lg font-medium text-gray-900 hover:text-primary-600 flex items-center gap-2"
                          >
                            {order.collectible.name}
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
                            <span className="text-sm text-gray-500">
                              Order #{order.id.slice(0, 8)}
                            </span>
                            <span className="hidden sm:inline text-sm text-gray-500">•</span>
                            <time className="text-sm text-gray-500">
                              {format(new Date(order.created_at), 'PPp')}
                            </time>
                          </div>
                        </div>
                        <div className="text-xl font-bold text-gray-900 flex items-center gap-1">
                          <IndianRupee className="h-5 w-5" />
                          {order.collectible.price.toLocaleString()}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            Customer Details
                          </h4>
                          <div className="space-y-1">
                            <p className="text-gray-900 font-medium">
                              {order.full_name}
                            </p>
                            <a
                              href={`tel:${order.phone}`}
                              className="text-gray-600 flex items-center gap-2 hover:text-primary-600"
                            >
                              <Phone className="h-4 w-4" />
                              {order.phone}
                            </a>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            Shipping Address
                          </h4>
                          <div className="space-y-1 text-gray-600">
                            <p>{order.address_line1}</p>
                            {order.address_line2 && <p>{order.address_line2}</p>}
                            <p>
                              {order.city}, {order.state} {order.pincode}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t border-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">Status:</span>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeColor(order.status)}`}>
                              {getStatusIcon(order.status)}
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                              order.collectible.status === 'unavailable'
                                ? 'bg-gray-50 text-gray-600'
                                : order.collectible.status === 'sold'
                                ? 'bg-red-50 text-red-600'
                                : 'bg-green-50 text-green-600'
                            }`}>
                              <Tag className="h-4 w-4" />
                              {order.collectible.status.charAt(0).toUpperCase() + order.collectible.status.slice(1)}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-3">
                            {updatingStatus === order.id ? (
                              <Loader className="h-5 w-5 animate-spin text-primary-600" />
                            ) : (
                              <>
                                <button
                                  onClick={() => updateOrderStatus(order.id, 'fulfilled')}
                                  disabled={order.status === 'fulfilled'}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center gap-2"
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                  <span>Mark Fulfilled</span>
                                </button>
                                <button
                                  onClick={() => updateOrderStatus(order.id, 'cancelled')}
                                  disabled={order.status === 'cancelled'}
                                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center flex items-center gap-2"
                                >
                                  <XCircle className="h-4 w-4" />
                                  <span>Cancel Order</span>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default AdminOrders;